import * as React from 'react'
import * as PropTypes from 'prop-types'
import { omit } from 'lodash-es'
import { css, Rule } from 'glamor'

// Start animation helper using nested requestAnimationFrames
function startAnimationHelper(callback: () => void) {
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => callback()))
}

function isNumber(n: unknown): n is number {
  return !isNaN(parseFloat(n as string)) && isFinite(n as number)
}

function runCallback(callback?: () => void) {
  if (callback && typeof callback === 'function') {
    return callback()
  }
}

interface IAnimateHeightProps {
  children: React.ReactNode

  animateOpacity: boolean
  applyInlineTransitions: boolean
  delay: number
  duration: number
  easing: string

  animationStateClasses?: object
  className?: string
  contentStyle?: Rule
  height?: string | number
  innerRef?: React.RefObject<HTMLDivElement> | ((ref: HTMLDivElement) => void)
  onAnimationEnd?: () => void
  onAnimationStart?: () => void
  style?: React.CSSProperties
}

interface IAnimateHeightState {
  height: string | number
  overflow: string | null
  shouldUseTransitions: boolean
}

export class AnimateHeight extends React.Component<IAnimateHeightProps, IAnimateHeightState> {
  static propTypes = {
    animateOpacity: PropTypes.bool,
    animationStateClasses: PropTypes.object,
    applyInlineTransitions: PropTypes.bool,
    children: PropTypes.any.isRequired,
    className: PropTypes.string,
    innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    contentStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    duration: PropTypes.number,
    delay: PropTypes.number,
    easing: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onAnimationEnd: PropTypes.func,
    onAnimationStart: PropTypes.func,
    style: PropTypes.object,
  }

  static defaultProps = {
    animateOpacity: true,
    applyInlineTransitions: true,
    duration: 200,
    delay: 0,
    easing: 'ease-in-out',
    style: {},
  }

  constructor(props: IAnimateHeightProps) {
    super(props)

    let height: string | number = 'auto'
    let overflow = 'visible'

    if (isNumber(props.height)) {
      height = props.height < 0 ? 0 : props.height
      overflow = 'hidden'
    } else if (
      // Percentage height
      typeof props.height === 'string' &&
      props.height.search('%') === props.height.length - 1 &&
      isNumber(props.height.substr(0, props.height.length - 1))
    ) {
      height = props.height
      overflow = 'hidden'
    }

    this.state = {
      height,
      overflow,
      shouldUseTransitions: false,
    }

    this.contentElement = React.createRef()
  }

  componentDidMount() {
    const { height } = this.state

    // Hide content if height is 0 (to prevent tabbing into it)
    // Check for contentElement is added cause this would fail in tests (react-test-renderer)
    // Read more here: https://github.com/Stanko/react-animate-height/issues/17
    if (this.contentElement.current && this.contentElement.current.style) {
      this.hideContent(height)
    }
  }

  componentDidUpdate(prevProps: IAnimateHeightProps) {
    const { height, onAnimationEnd, onAnimationStart } = this.props

    const delay = this.mediaQuery && this.mediaQuery.matches ? 0 : this.props.delay
    const duration = this.mediaQuery && this.mediaQuery.matches ? 0 : this.props.duration

    // Check if 'height' prop has changed
    if (this.contentElement.current && height !== prevProps.height) {
      // Remove display: none from the content div
      // if it was hidden to prevent tabbing into it
      this.showContent()

      // Cache content height
      this.contentElement.current.style.overflow = 'hidden'
      const contentHeight = this.contentElement.current.offsetHeight
      this.contentElement.current.style.overflow = ''

      // set total animation time
      const totalDuration = duration + delay

      let newHeight: IAnimateHeightState['height']
      const timeoutState: Pick<IAnimateHeightState, 'height' | 'overflow' | 'shouldUseTransitions'> = {
        height: 0, // it will be always set to either 'auto' or specific number
        overflow: 'hidden',
        shouldUseTransitions: false,
      }
      const isCurrentHeightAuto = this.state.height === 'auto'

      if (isNumber(height)) {
        // If new height is a number
        newHeight = height < 0 ? 0 : height
        timeoutState.height = newHeight
      } else if (
        // Percentage height
        typeof height === 'string' &&
        height.search('%') === height.length - 1 &&
        isNumber(height.substr(0, height.length - 1))
      ) {
        newHeight = height
        timeoutState.height = newHeight
      } else {
        // If not, animate to content height
        // and then reset to auto
        newHeight = contentHeight
        timeoutState.height = 'auto'
        timeoutState.overflow = null
      }

      if (isCurrentHeightAuto) {
        // This is the height to be animated to
        timeoutState.height = newHeight

        // If previous height was 'auto'
        // set starting height explicitly to be able to use transition
        newHeight = contentHeight
      }

      // We are safe to call set state as it will not trigger infinite loop
      // because of the "height !== prevProps.height" check
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        height: newHeight,
        overflow: 'hidden',
        // When animating from 'auto' we first need to set fixed height
        // that change should be animated
        shouldUseTransitions: !isCurrentHeightAuto,
      })

      // Clear timeouts
      if (this.timeoutID) {
        window.clearTimeout(this.timeoutID)
      }

      if (this.animationClassesTimeoutID) {
        window.clearTimeout(this.animationClassesTimeoutID)
      }

      if (isCurrentHeightAuto) {
        // When animating from 'auto' we use a short timeout to start animation
        // after setting fixed height above
        timeoutState.shouldUseTransitions = true

        startAnimationHelper(() => {
          this.setState(timeoutState)

          // ANIMATION STARTS, run a callback if it exists
          runCallback(onAnimationStart)
        })

        // Set static classes and remove transitions when animation ends
        this.animationClassesTimeoutID = window.setTimeout(() => {
          this.setState({
            shouldUseTransitions: false,
          })

          // ANIMATION ENDS
          // Hide content if height is 0 (to prevent tabbing into it)
          this.hideContent(timeoutState.height)
          // Run a callback if it exists
          runCallback(onAnimationEnd)
        }, totalDuration)
      } else {
        // ANIMATION STARTS, run a callback if it exists
        runCallback(onAnimationStart)

        // Set end height, classes and remove transitions when animation is complete
        this.timeoutID = window.setTimeout(() => {
          timeoutState.shouldUseTransitions = false

          this.setState(timeoutState)

          // ANIMATION ENDS
          // Hide content if height is 0 (to prevent tabbing into it)
          this.hideContent(newHeight)
          // Run a callback if it exists
          runCallback(onAnimationEnd)
        }, totalDuration)
      }
    }
  }

  componentWillUnmount() {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID)
    }

    if (this.animationClassesTimeoutID) {
      clearTimeout(this.animationClassesTimeoutID)
    }

    this.timeoutID = null
    this.animationClassesTimeoutID = null
  }

  mediaQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : undefined
  contentElement: React.RefObject<HTMLDivElement>
  timeoutID: number | null = null
  animationClassesTimeoutID: number | null = null

  showContent() {
    if (this.contentElement.current && this.state.height === 0) {
      this.contentElement.current.style.display = ''
    }
  }

  hideContent(newHeight?: string | number) {
    if (this.contentElement.current && newHeight === 0) {
      this.contentElement.current.style.display = 'none'
    }
  }

  attachRef = (el: HTMLDivElement) => {
    ;(this.contentElement.current as any) = el

    if (this.props.innerRef && typeof this.props.innerRef === 'function') {
      this.props.innerRef(el)
    } else if (this.props.innerRef) {
      ;(this.props.innerRef.current as any) = el
    }
  }

  render() {
    const { animateOpacity, applyInlineTransitions, children, contentStyle, easing, style } = this.props
    const { height, overflow, shouldUseTransitions } = this.state

    const delay = this.mediaQuery && this.mediaQuery.matches ? 0 : this.props.delay
    const duration = this.mediaQuery && this.mediaQuery.matches ? 0 : this.props.duration

    const componentStyle: { [key: string]: any } = {
      ...style,
      height,
      overflow: overflow || (style as any).overflow,
    }

    if (shouldUseTransitions && applyInlineTransitions) {
      componentStyle.transition = `height ${duration}ms ${easing} ${delay}ms`

      // Include transition passed through styles
      if ((style as any).transition) {
        componentStyle.transition = `${(style as any).transition}, ${componentStyle.transition}`
      }
    }

    const internalContentStyle = {}

    if (animateOpacity) {
      ;(internalContentStyle as any).transition = `opacity ${duration}ms ${easing} ${delay}ms`
      // Add webkit vendor prefix still used by opera, blackberry...
      ;(internalContentStyle as any).WebkitTransition = (internalContentStyle as any).transition

      if (height === 0) {
        ;(internalContentStyle as any).opacity = 0
      }
    }

    const propsToOmit = [
      'animateOpacity',
      'animationStateClasses',
      'applyInlineTransitions',
      'contentStyle',
      'innerRef',
      'duration',
      'easing',
      'height',
      'delay',
    ]

    return (
      <div {...omit(this.props, propsToOmit)} aria-hidden={height === 0} {...css(componentStyle)}>
        <div {...css(contentStyle, internalContentStyle)} ref={this.attachRef}>
          {children}
        </div>
      </div>
    )
  }
}
